import React from "react";
import { useLocation } from "react-router-dom";

import { useIsMobile } from "src/common/hooks/useIsMobile";

export function useSidebarState(
	key: string,
	isResponsive: boolean
): { isMobile: boolean; isCollapsed: boolean; onToggle(): void } {
	const keyRef = React.useRef(key);
	const isResponsiveRef = React.useRef(isResponsive);

	const { pathname } = useLocation();
	const isMobile = useIsMobile();

	const savedValue = localStorage.getItem(keyRef.current);
	const [isCollapsed, setCollapsed] = React.useState(
		savedValue === undefined
			? isResponsiveRef.current && isSmallScreen()
			: savedValue === "true"
	);

	React.useEffect(() => {
		if (isResponsiveRef.current) {
			const handler = (): void => {
				setCollapsed(isSmallScreen());
			};
			window.addEventListener("resize", handler);
			return () => {
				window.removeEventListener("resize", handler);
			};
		}
	}, []);

	React.useEffect(() => {
		localStorage.setItem(keyRef.current, `${isCollapsed}`);
	}, [isCollapsed]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: auto-collapse sidebar on route change (mobile)
	React.useEffect(() => {
		if (isMobile) {
			setCollapsed(true);
		}
	}, [pathname, isMobile]);

	const onToggle = React.useCallback(() => {
		setCollapsed(isCollapsed => !isCollapsed);
	}, []);

	return { isMobile, isCollapsed, onToggle };
}

function isSmallScreen(): boolean {
	return window.innerWidth < 1280;
}
